import type { ProductKey } from '@/router/products'

/**
 * Maps a bundle's `reference_mysmerra` (stable business reference carried by a
 * contract) to a front-end formula slug — the key under which guarantee tables
 * live in `guaranteeDetails.ts`.
 *
 * Source of truth: the backend bundle catalogue (`lib/assets/bundled_products.csv`,
 * references 1..36). Combined packs (base + add-on) point to their base formula;
 * references with no guarantee table on the front are intentionally absent
 * (the mapper then shows no guarantees rather than guessing).
 */
export const BUNDLE_REFERENCE_TO_PLAN: Record<number, string> = {
  1: 'minimal',
  2: 'essential',
  3: 'confort',
  5: 'allRisk',
  6: 'indispensable',
  7: 'security',
  8: 'tonic',
  11: 'primordiale',
  12: 'renforcee',
  27: 'packEurope',
  28: 'packMonde',
  4: 'packFranceConfort',
  33: 'packFranceGlobal',
  34: 'solidariteExam',
  // Combined packs → base formula's guarantees
  9: 'minimal',
  10: 'minimal',
  15: 'essential',
  16: 'essential',
  17: 'confort',
  18: 'confort',
  19: 'allRisk',
  20: 'allRisk',
  25: 'tonic',
  26: 'tonic',
  13: 'primordiale',
  35: 'primordiale',
  14: 'renforcee',
  36: 'renforcee',
}

/** The formula slug for a bundle reference, or `null` when unmapped. */
export function planSlugForBundleReference(reference: number | null | undefined): string | null {
  if (reference == null) return null
  return BUNDLE_REFERENCE_TO_PLAN[reference] ?? null
}

/**
 * Maps a bundle's `normalized_name` (`bundle.name.parameterize('_')`, carried by
 * a contract as `bundle_normalized_name`) to the journey it belongs to. Used by
 * the connected space to tell whether a given product is already subscribed.
 *
 * Source of truth: the backend bundle catalogue, grouped by `sous_categorie` in
 * `lib/assets/bundled_products.csv` — one subcategory per `ProductKey`. Includes
 * the add-on packs (`*_indispensable` / `*_securite`) a contract can carry. Names
 * absent here (e.g. child half-price variants not offered in a journey) resolve
 * to `null` and are simply treated as "no matching product".
 */
const NORMALIZED_NAME_TO_PRODUCT: Record<string, ProductKey> = {
  // COMPL_SANTE → dépenses santé
  minimale: 'healthExpenses',
  minimale_indispensable: 'healthExpenses',
  minimale_securite: 'healthExpenses',
  essentielle: 'healthExpenses',
  essentielle_indispensable: 'healthExpenses',
  essentielle_securite: 'healthExpenses',
  confort: 'healthExpenses',
  confort_indispensable: 'healthExpenses',
  confort_securite: 'healthExpenses',
  tous_risques: 'healthExpenses',
  tous_risques_indispensable: 'healthExpenses',
  tous_risques_securite: 'healthExpenses',
  // AVE → responsabilité civile
  indispensable: 'civil',
  securite: 'civil',
  // SURCOMPL_SANTE → renforcer sa santé
  'tonic-etudes': 'healthTopUp',
  tonicetudes_indispensable: 'healthTopUp',
  tonicetudes_securite: 'healthTopUp',
  // AURA → mutuelle AURA
  primordiale: 'regionalAura',
  primordiale_indispensable: 'regionalAura',
  primordiale_securite: 'regionalAura',
  renforcee: 'regionalAura',
  renforcee_indispensable: 'regionalAura',
  renforcee_securite: 'regionalAura',
  // PME → étudier à l'étranger
  pack_europe: 'abroad',
  pack_monde: 'abroad',
  // PF → arrivée en France
  pack_en_france_confort: 'arrivingFrance',
  pack_en_france_global: 'arrivingFrance',
  // SOLIDARITE_EXAM → solidarité exam
  solidarite_exam: 'solidariteExam',
}

/** The `ProductKey` a contract's bundle belongs to, or `null` when unmapped. */
export function productKeyForNormalizedName(
  normalizedName: string | null | undefined,
): ProductKey | null {
  if (!normalizedName) return null
  return NORMALIZED_NAME_TO_PRODUCT[normalizedName] ?? null
}
